import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";

interface SplashProps {
  onComplete: () => void;
}

const Splash: React.FC<SplashProps> = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<"loading" | "exit">("loading");

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          clearInterval(interval);
          setPhase("exit");
          setTimeout(onComplete, 800);
          return 100;
        }
        return p + Math.random() * 6 + 2;
      });
    }, 90);
    return () => clearInterval(interval);
  }, [onComplete]);

  return (
    <SplashContainer $phase={phase}>
      <MandalaOuter>
        <MandalaRing $size={320} $duration={20} $dir={1} />
        <MandalaRing $size={240} $duration={14} $dir={-1} />
        <MandalaRing $size={160} $duration={9} $dir={1} />
      </MandalaOuter>
      <PortalGlow />
      <SplashContent>
        <EyeSymbol>&#9672;</EyeSymbol>
        <LogoText>
          {"SHAURYA".split("").map((ch, i) => (
            <LetterSpan key={i} $i={i}>
              {ch}
            </LetterSpan>
          ))}
        </LogoText>
        <RuneText>SINGHAL</RuneText>
        <LoaderTrack>
          <LoaderFill style={{ width: `${Math.min(progress, 100)}%` }} />
        </LoaderTrack>
        <StatusText>
          {progress < 25
            ? "Tapping the bricks of Diagon Alley..."
            : progress < 50
            ? "Boarding the Hogwarts Express..."
            : progress < 75
            ? "Crossing the Black Lake..."
            : progress < 100
            ? "The castle doors are opening..."
            : "Welcome to Hogwarts."}
        </StatusText>
      </SplashContent>
    </SplashContainer>
  );
};

export default Splash;

const portalSpin = keyframes`
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
`;

const letterIn = keyframes`
  0% { opacity: 0; transform: translateY(30px) rotateX(90deg); }
  100% { opacity: 1; transform: translateY(0) rotateX(0deg); }
`;

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.3; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.15); }
`;

const fadeOut = keyframes`
  to { opacity: 0; transform: scale(1.1); }
`;

const SplashContainer = styled.div<{ $phase: string }>`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #0a0612;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${({ $phase }) => ($phase === "exit" ? fadeOut : "none")} 0.8s ease-out forwards;
`;

const MandalaOuter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const MandalaRing = styled.div<{ $size: number; $duration: number; $dir: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border: 1px solid rgba(255, 140, 0, 0.15);
  border-top: 2px solid rgba(255, 140, 0, 0.5);
  border-radius: 50%;
  animation: ${portalSpin} ${({ $duration }) => $duration}s linear infinite;
  animation-direction: ${({ $dir }) => ($dir > 0 ? "normal" : "reverse")};
  transform-origin: center;
  margin-left: ${({ $size }) => -$size / 2}px;
  margin-top: ${({ $size }) => -$size / 2}px;

  &::before {
    content: "";
    position: absolute;
    inset: -1px;
    border: 1px dashed rgba(255, 215, 0, 0.08);
    border-radius: 50%;
  }
`;

const PortalGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 140, 0, 0.08), transparent 70%);
  animation: ${pulseGlow} 3s ease-in-out infinite;
  filter: blur(40px);
`;

const SplashContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const EyeSymbol = styled.div`
  font-size: 2.5rem;
  color: #00ff88;
  text-shadow: 0 0 20px rgba(0, 255, 136, 0.5), 0 0 40px rgba(0, 255, 136, 0.2);
  margin-bottom: 0.5rem;
`;

const LogoText = styled.h1`
  font-family: "Cinzel", serif;
  font-size: clamp(2.5rem, 8vw, 5rem);
  font-weight: 700;
  letter-spacing: 0.35em;
  color: #ffd700;
  text-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
  display: flex;
`;

const LetterSpan = styled.span<{ $i: number }>`
  display: inline-block;
  animation: ${letterIn} 0.6s ease-out both;
  animation-delay: ${({ $i }) => 0.1 + $i * 0.08}s;
`;

const RuneText = styled.p`
  font-family: "Cinzel", serif;
  font-size: clamp(0.9rem, 2vw, 1.3rem);
  letter-spacing: 0.8em;
  color: rgba(255, 140, 0, 0.6);
  margin-top: -0.3rem;
`;

const LoaderTrack = styled.div`
  width: 240px;
  height: 2px;
  background: rgba(255, 140, 0, 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 1.5rem;
`;

const LoaderFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #ff8c00, #ffd700);
  border-radius: 2px;
  transition: width 0.15s ease;
  box-shadow: 0 0 12px rgba(255, 140, 0, 0.5);
`;

const StatusText = styled.p`
  font-family: "Cinzel", serif;
  font-size: 0.72rem;
  color: #7a6b5d;
  letter-spacing: 0.2em;
`;
